import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';

type Portal = 'agent' | 'partner' | 'client';

interface Props {
  children: React.ReactNode;
  /**
   * Portals allowed to view this route. Defaults to ['agent'] — most pages are
   * for the internal team only. Pass ['agent', 'partner'] for pages a lender
   * should also be able to see (transactions list/detail, dashboard).
   */
  portals?: Portal[];
}

/**
 * Wraps protected routes. Behavior:
 *  - while AuthProvider is hydrating, render nothing (prevents flash-redirects)
 *  - if no user, send to /login with a `from` state so we can return there post-login
 *  - if user.must_change_password and we're not already on /change-password, force the redirect
 *  - if the route is not allowed for this user's portal, send them to the home dashboard
 */
export default function RequireAuth({ children, portals }: Props) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  const allowed = portals ?? ['agent'];
  if (!allowed.includes(user.portal as Portal)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
